import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchReviewsThumbnailPhotos } from "slices/reviews-thumbnail-carousel-slice";

const THUMBNAIL_PAGE_SIZE = 20;
const SCROLL_BUFFER_PX = 300;

export function useLazyThumbnailLoader(containerRef, productId, scrollBufferPx = SCROLL_BUFFER_PX) {
  const dispatch = useDispatch();
  const momentsWithCount = useSelector(state => state.reviewsThumbnailPhotos);
  const reviewsThumbnailPhotos = momentsWithCount?.moments;
  const totalCount = momentsWithCount?.totalCount || 0;
  const loading = momentsWithCount?.loading;
  const lastScrollLeft = useRef(0);
  const triggered = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || totalCount <= reviewsThumbnailPhotos?.length) return;

    const onScroll = () => {
      const scrollLeft = container.scrollLeft;
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const scrollingForward = scrollLeft > lastScrollLeft.current;
      lastScrollLeft.current = scrollLeft;

      const isNearEnd = maxScrollLeft - scrollLeft <= scrollBufferPx;

      if (
        scrollingForward &&
        isNearEnd &&
        !triggered.current &&
        !loading &&
        reviewsThumbnailPhotos?.length < totalCount
      ) {
        triggered.current = true;
        dispatch(
          fetchReviewsThumbnailPhotos({
            productId,
            offset: reviewsThumbnailPhotos.length,
            limit: THUMBNAIL_PAGE_SIZE,
            orderBy: "newest",
          })
        );
      }

      if (!isNearEnd) {
        triggered.current = false;
      }
    };

    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, [containerRef, productId, dispatch, reviewsThumbnailPhotos?.length, totalCount, loading]);
}

export function useLoadThumbnailPhotos(productId) {
  const dispatch = useDispatch();
  const momentsWithCount = useSelector(state => state.reviewsThumbnailPhotos);
  const reviewsThumbnailPhotos = momentsWithCount?.moments;
  const loading = momentsWithCount?.loading;

  const hasReviewCoverPhotoForStyle =
    Array.isArray(reviewsThumbnailPhotos) &&
    reviewsThumbnailPhotos.length > 0 &&
    reviewsThumbnailPhotos[0]?.styleName === productId; // this extra clause is to support product drawers

  // on load, fetch first 20 images if not already loaded
  useEffect(() => {
    if (!hasReviewCoverPhotoForStyle && !loading) {
      dispatch(
        fetchReviewsThumbnailPhotos({
          productId,
          offset: 0,
          limit: THUMBNAIL_PAGE_SIZE,
          orderBy: "newest",
        })
      );
    }
  }, []);
}

// This is for old PDPs that can't use the hook directly, can be removed once we are fully migrated to uPdp
export function loadThumbnailPhotos(productId, reviewsThumbnailPhotosObject, dispatch) {
  const reviewsThumbnailPhotos = reviewsThumbnailPhotosObject?.moments;
  const loading = reviewsThumbnailPhotos?.loading;

  const hasReviewCoverPhotoForStyle =
    Array.isArray(reviewsThumbnailPhotos) &&
    reviewsThumbnailPhotos.length > 0 &&
    reviewsThumbnailPhotos[0]?.styleName === productId; // this extra clause is to support product drawers

  // on load, fetch first 20 images if not already loaded
  if (!hasReviewCoverPhotoForStyle && !loading) {
    dispatch(
      fetchReviewsThumbnailPhotos({
        productId,
        offset: 0,
        limit: THUMBNAIL_PAGE_SIZE,
        orderBy: "newest",
      })
    );
  }
}
